"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.apiForCurrency = void 0;
const url_1 = __importDefault(require("url"));
const invariant_1 = __importDefault(require("invariant"));
const bignumber_js_1 = require("bignumber.js");
const errors_1 = require("@ledgerhq/errors");
const network_1 = __importDefault(require("@ledgerhq/live-network/network"));
const cache_1 = require("@ledgerhq/live-network/cache");
const transaction_1 = require("../transaction");
const explorer_1 = require("../../../explorer");
const errors_2 = require("../../../errors");
const live_env_1 = require("@ledgerhq/live-env");
const apiForCurrency = (currency) => {
    const baseURL = (0, explorer_1.blockchainBaseURL)(currency);
    if (!baseURL) {
        throw new errors_1.LedgerAPINotAvailable(`LedgerAPINotAvailable ${currency.id}`, {
            currencyName: currency.name,
        });
    }
    return {
        getTransactions(address, blockHeight, batchSize = 2000, token) {
            return __awaiter(this, void 0, void 0, function* () {
                const query = {
                    batch_size: batchSize,
                    filtering: true,
                    noinput: true,
                };
                if (blockHeight) {
                    query.from_height = blockHeight;
                    query.order = "ascending"; // Needed to make sure we get transactions after the block height and not before. Order is still descending in the end
                }
                if (token) {
                    query.token = token;
                }
                const txData = yield (0, network_1.default)({
                    method: "GET",
                    url: url_1.default.format({
                        pathname: `${baseURL}/address/${address}/txs`,
                        query,
                    }),
                });
                return { txs: txData.data.data, nextPageToken: txData.data.token };
            });
        },
        getCurrentBlock() {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "GET",
                    url: `${baseURL}/block/current`,
                });
                const { height, hash, time, txs } = data;
                return {
                    hash,
                    time,
                    txs,
                    height: new bignumber_js_1.BigNumber(height),
                };
            });
        },
        getAccountNonce(address) {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "GET",
                    url: `${baseURL}/address/${address}/nonce`,
                });
                return data.nonce;
            });
        },
        broadcastTransaction(tx) {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "POST",
                    url: `${baseURL}/tx/send`,
                    data: {
                        tx,
                    },
                });
                return data.result;
            });
        },
        getTransactionByHash(hash) {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "GET",
                    url: `${baseURL}/tx/${hash}`,
                });
                return data;
            });
        },
        getAccountBalance(address) {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "GET",
                    url: `${baseURL}/address/${address}/balance`,
                });
                return new bignumber_js_1.BigNumber(data.balance);
            });
        },
        getERC20Balances(input) {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "POST",
                    url: `${baseURL}/erc20/balances`,
                    data: input,
                });
                return data.map(({ address, contract, balance }) => ({
                    address,
                    contract,
                    balance: new bignumber_js_1.BigNumber(balance),
                }));
            });
        },
        getNftMetadata(input, { chainId }) {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "POST",
                    url: `${(0, live_env_1.getEnv)("NFT_ETH_METADATA_SERVICE")}/v1/ethereum/${chainId}/contracts/tokens/infos`,
                    data: input,
                });
                return data;
            });
        },
        getNftCollectionMetadata(input, { chainId }) {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "POST",
                    url: `${(0, live_env_1.getEnv)("NFT_ETH_METADATA_SERVICE")}/v1/ethereum/${chainId}/contracts/infos`,
                    data: input,
                });
                return data;
            });
        },
        getERC20ApprovalsPerContract(owner, contract) {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    const { data } = yield (0, network_1.default)({
                        method: "GET",
                        url: url_1.default.format({
                            pathname: `${baseURL}/erc20/approvals`,
                            query: {
                                owner,
                                contract,
                            },
                        }),
                    });
                    return data
                        .map((m) => {
                        if (!m || typeof m !== "object")
                            return;
                        const { spender, count: value } = m;
                        if (typeof spender !== "string" || typeof value !== "string")
                            return;
                        return {
                            sender: spender,
                            value,
                        };
                    })
                        .filter(Boolean);
                }
                catch (e) {
                    if (e.status === 404) {
                        return [];
                    }
                    throw e;
                }
            });
        },
        // FIXME: Dirty fix that calls v3 gas limit estimation while we find a better solution
        getFallbackGasLimit(address) {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "GET",
                    url: `${baseURL.replace("v4", "v3")}/addresses/${address}/estimate-gas-limit`,
                });
                return new bignumber_js_1.BigNumber(data.estimated_gas_limit);
            });
        },
        getDryRunGasLimit(transaction) {
            return __awaiter(this, void 0, void 0, function* () {
                const { data } = yield (0, network_1.default)({
                    method: "POST",
                    url: `${baseURL}/tx/estimate-gas-limit`,
                    data: transaction,
                });
                if (data.error_message) {
                    throw new errors_2.FeeEstimationFailed(data.error_message);
                }
                const value = new bignumber_js_1.BigNumber(data.estimated_gas_limit);
                (0, invariant_1.default)(!value.isNaN(), "invalid server data");
                return value;
            });
        },
        getGasTrackerBarometer: (0, cache_1.makeLRUCache)((currency) => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield (0, network_1.default)({
                method: "GET",
                url: `${baseURL}/gastracker/barometer${(0, transaction_1.EIP1559ShouldBeUsed)(currency) ? "?display=eip1559" : ""}`,
            });
            return {
                low: new bignumber_js_1.BigNumber(data.low),
                medium: new bignumber_js_1.BigNumber(data.medium),
                high: new bignumber_js_1.BigNumber(data.high),
                next_base: new bignumber_js_1.BigNumber(data.next_base),
            };
        }), currency => currency.id, {
            ttl: 30 * 1000,
        }),
        getBlockByHash(blockHash) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!blockHash) {
                    return undefined;
                }
                try {
                    const { data } = yield (0, network_1.default)({
                        method: "GET",
                        url: `${baseURL}/block/${blockHash}`,
                    });
                    return data[0];
                }
                catch (e) {
                    return undefined;
                }
            });
        },
    };
};
exports.apiForCurrency = apiForCurrency;
//# sourceMappingURL=index.js.map